import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { AvatarStack } from '../../components';
import { ClassroomGroup } from '../../boutique-classroom.types';
import { GROUP_NAME_MAX_LENGTH, STUDENT_AVATAR_TONE_COUNT } from '../../boutique-classroom.constants';
import { BoutiqueStudent } from '../boutique-students.types';
import {
	GROUP_DRAWER_ADD_DONE_LABEL,
	GROUP_DRAWER_ADD_EMPTY_TEXT,
	GROUP_DRAWER_ADD_ROW_LABEL,
	GROUP_DRAWER_ADD_SEARCH_PLACEHOLDER,
	GROUP_DRAWER_ADD_SECTION_TITLE,
	GROUP_DRAWER_ADD_STUDENT_LABEL,
	GROUP_DRAWER_AVATAR_LIMIT,
	GROUP_DRAWER_SEARCH_PLACEHOLDER,
	GROUP_DRAWER_STUDENTS_TAB_LABEL,
	GROUP_NAME_INPUT_ID
} from '../boutique-students.constants';
import '@/assets/styles/views/boutique-classroom/group-detail-drawer.styles.scss';

interface GroupDetailDrawerProps {
	group: ClassroomGroup | null;
	students: BoutiqueStudent[];
	availableStudents?: BoutiqueStudent[];
	isMutating?: boolean;
	onClose: () => void;
	onRename: (groupId: number, name: string) => void;
	onRemoveExpert?: (groupMemberId: number) => void;
	onAddStudent?: (groupId: number, studentId: number) => void;
	onRemoveStudent?: (studentId: number) => void;
}

const ESCAPE_KEY = 'Escape';
const ENTER_KEY = 'Enter';
const EMPTY_STUDENTS: BoutiqueStudent[] = [];

function getInitials(fullName: string): string {
	return fullName
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(part => part.charAt(0).toLocaleUpperCase('tr-TR'))
		.join('');
}

function normalize(value: string): string {
	return value.toLocaleLowerCase('tr-TR').trim();
}

function matchesQuery(student: BoutiqueStudent, needle: string): boolean {
	return normalize(student.fullName).includes(needle) || normalize(student.schoolName).includes(needle);
}

export const GroupDetailDrawer: React.FC<GroupDetailDrawerProps> = ({
	group,
	students,
	availableStudents = EMPTY_STUDENTS,
	isMutating = false,
	onClose,
	onRename,
	onRemoveExpert,
	onAddStudent,
	onRemoveStudent
}) => {
	const isOpen = group !== null;
	const [query, setQuery] = useState('');
	const [isAddMode, setIsAddMode] = useState(false);
	const [isEditingName, setIsEditingName] = useState(false);
	const [draftName, setDraftName] = useState('');
	const nameInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setQuery('');
		setIsAddMode(false);
		setIsEditingName(false);
		setDraftName(group?.name ?? '');
	}, [group?.id, group?.name]);

	useEffect(() => {
		if (isEditingName) nameInputRef.current?.focus();
	}, [isEditingName]);

	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === ESCAPE_KEY && !isEditingName) onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isEditingName, isOpen, onClose]);

	const needle = normalize(query);
	const filteredStudents = useMemo(() => (needle ? students.filter(student => matchesQuery(student, needle)) : students), [needle, students]);
	const filteredAvailableStudents = useMemo(
		() => (needle ? availableStudents.filter(student => matchesQuery(student, needle)) : availableStudents),
		[availableStudents, needle]
	);

	const commitName = useCallback(() => {
		if (!group) return;
		const next = draftName.trim();
		if (next && next !== group.name) onRename(group.id, next);
		setDraftName(next || group.name);
		setIsEditingName(false);
	}, [draftName, group, onRename]);

	const cancelEdit = useCallback(() => {
		setDraftName(group?.name ?? '');
		setIsEditingName(false);
	}, [group?.name]);

	const handleNameKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === ENTER_KEY) {
				event.preventDefault();
				commitName();
			} else if (event.key === ESCAPE_KEY) {
				event.preventDefault();
				cancelEdit();
			}
		},
		[cancelEdit, commitName]
	);

	const toggleAddMode = useCallback(() => {
		setIsAddMode(prev => !prev);
		setQuery('');
	}, []);

	const canAddStudents = Boolean(onAddStudent);
	const canRemoveStudents = Boolean(onRemoveStudent);

	const renderStudentRow = (student: BoutiqueStudent, action: React.ReactNode) => (
		<li
			key={student.id}
			className="group-drawer__student">
			<span className={clsx('group-drawer__avatar', `group-drawer__avatar--tone-${student.id % STUDENT_AVATAR_TONE_COUNT}`)}>{getInitials(student.fullName)}</span>
			<span className="group-drawer__student-text">
				<span className="group-drawer__student-name">{student.fullName}</span>
				<span className="group-drawer__student-meta">{student.schoolName}</span>
			</span>
			{action}
		</li>
	);

	return (
		<aside
			className={clsx('group-drawer', { 'group-drawer--open': isOpen })}
			aria-hidden={!isOpen}
			aria-label={group ? `${group.name} grubu` : undefined}>
			{group && (
				<>
					<header className="group-drawer__header">
						<div className="group-drawer__title-row">
							{isEditingName ? (
								<input
									ref={nameInputRef}
									id={GROUP_NAME_INPUT_ID}
									className="group-drawer__name-input"
									value={draftName}
									maxLength={GROUP_NAME_MAX_LENGTH}
									onChange={event => setDraftName(event.target.value)}
									onBlur={commitName}
									onKeyDown={handleNameKeyDown}
									aria-label="Grup adı"
								/>
							) : (
								<>
									<h2 className="group-drawer__title">{group.name}</h2>
									<button
										type="button"
										className="group-drawer__edit"
										aria-label="Grup adını düzenle"
										onClick={() => setIsEditingName(true)}>
										<i className="icons-pen" />
									</button>
								</>
							)}
						</div>
						<button
							type="button"
							className="group-drawer__close"
							aria-label="Paneli kapat"
							onClick={onClose}>
							<X size={18} />
						</button>
					</header>

					<p className="group-drawer__count">{students.length} öğrenci</p>
					<AvatarStack
						className="group-drawer__avatars"
						students={students.map(student => ({ id: student.id, fullName: student.fullName }))}
						maxVisible={GROUP_DRAWER_AVATAR_LIMIT}
					/>

					<div
						className="group-drawer__tabs"
						role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected="true"
							className="group-drawer__tab group-drawer__tab--active">
							{GROUP_DRAWER_STUDENTS_TAB_LABEL}
						</button>
					</div>

					<div className="group-drawer__toolbar">
						<label className="group-drawer__search">
							<i className="icons-minimalistic-magnifer" />
							<input
								type="search"
								value={query}
								onChange={event => setQuery(event.target.value)}
								placeholder={isAddMode ? GROUP_DRAWER_ADD_SEARCH_PLACEHOLDER : GROUP_DRAWER_SEARCH_PLACEHOLDER}
								aria-label={isAddMode ? GROUP_DRAWER_ADD_SEARCH_PLACEHOLDER : GROUP_DRAWER_SEARCH_PLACEHOLDER}
							/>
						</label>
						{canAddStudents && (
							<button
								type="button"
								className={clsx('group-drawer__add-toggle', { 'group-drawer__add-toggle--active': isAddMode })}
								aria-pressed={isAddMode}
								disabled={isMutating}
								onClick={toggleAddMode}>
								{!isAddMode && <i className="icons-plus" />}
								{isAddMode ? GROUP_DRAWER_ADD_DONE_LABEL : GROUP_DRAWER_ADD_STUDENT_LABEL}
							</button>
						)}
					</div>

					{isAddMode ? (
						<>
							<p className="group-drawer__section-title">{GROUP_DRAWER_ADD_SECTION_TITLE}</p>
							{filteredAvailableStudents.length === 0 ? (
								<p className="group-drawer__empty">{GROUP_DRAWER_ADD_EMPTY_TEXT}</p>
							) : (
								<ul className="group-drawer__list">
									{filteredAvailableStudents.map(student =>
										renderStudentRow(
											student,
											<button
												type="button"
												className="group-drawer__student-add"
												aria-label={`${student.fullName} öğrencisini gruba ekle`}
												disabled={isMutating}
												onClick={() => onAddStudent?.(group.id, student.id)}>
												<i className="icons-plus" />
												{GROUP_DRAWER_ADD_ROW_LABEL}
											</button>
										)
									)}
								</ul>
							)}
						</>
					) : (
						<>
							{group.experts.length > 0 && (
								<>
									<p className="group-drawer__section-title">Akademik Kurul</p>
									<ul className="group-drawer__experts">
										{group.experts.map(expert => (
											<li
												key={expert.groupMemberId}
												className="group-drawer__student">
												<span className={clsx('group-drawer__avatar', `group-drawer__avatar--tone-${expert.id % STUDENT_AVATAR_TONE_COUNT}`)}>{getInitials(expert.fullName)}</span>
												<span className="group-drawer__student-text">
													<span className="group-drawer__student-name">{expert.fullName}</span>
												</span>
												{expert.branchName && <span className="group-drawer__expert-branch">{expert.branchName}</span>}
												{onRemoveExpert && (
													<button
														type="button"
														className="group-drawer__student-remove"
														aria-label={`${expert.fullName} uzmanını gruptan çıkar`}
														disabled={isMutating}
														onClick={() => onRemoveExpert(expert.groupMemberId)}>
														<i className="icons-minus" />
													</button>
												)}
											</li>
										))}
									</ul>
									<p className="group-drawer__section-title">Öğrenciler</p>
								</>
							)}
							{filteredStudents.length === 0 ? (
								<p className="group-drawer__empty">{students.length === 0 ? 'Bu grupta henüz öğrenci yok.' : 'Aramayla eşleşen öğrenci bulunamadı.'}</p>
							) : (
								<ul className="group-drawer__list">
									{filteredStudents.map(student =>
										renderStudentRow(
											student,
											canRemoveStudents ? (
												<button
													type="button"
													className="group-drawer__student-remove"
													aria-label={`${student.fullName} öğrencisini gruptan çıkar`}
													disabled={isMutating}
													onClick={() => onRemoveStudent?.(student.id)}>
													<i className="icons-minus" />
												</button>
											) : null
										)
									)}
								</ul>
							)}
						</>
					)}
				</>
			)}
		</aside>
	);
};

export default GroupDetailDrawer;
